// Phase Setup Chatops — compose resolve+persist Mattermost thành 1 flow fail-soft.
// Ported from avatar-po-cli phase-04 (identical flow). Gọi sau AI setup / GitNexus setup
// trong `scaffoldThinWorkspace` (workspace-scaffold-and-finalize-orchestrator.ts) — cùng
// pattern fail-soft đã dùng cho runAiSetupPhase/runGitnexusSetupPhase ở avatar CLI.
// Try/catch toàn bộ ở CALLER (orchestrator) — hàm này NÉM lỗi để caller catch + log warn,
// KHÔNG throw process.exit — workspace vẫn dùng được, user setup lại qua `avatar chatops-connect`.
//
// UX v2: hỏi Yes/No trước khi vào flow (không ép setup) → menu chọn kênh giao tiếp
// (hiện chỉ có Mattermost, chừa chỗ mở rộng) → nếu bot mặc định "avatar" đã có token
// lưu sẵn trong Keychain thì dùng luôn; miss thì thử lấy tự động từ server NAL
// (get-bot-token, cần đã `avatar login`) trước khi hỏi tay.
//
// UX v3: validate fail (channel không tìm thấy / token bị từ chối) → retry-loop
// menu thay vì ném lỗi ngay: "Điền lại tên channel"/"Nhập lại token" (re-prompt
// field liên quan rồi validate lại) hoặc "Setup lại sau" (defer, return
// {ok:false, reason:"deferred"} — KHÔNG throw, orchestrator không coi là fail).
import { confirm, input, password, select } from "@inquirer/prompts";
import {
  DEFAULT_BOT_NAME,
  DEFAULT_MM_URL,
  type MattermostCredentials,
  persistMattermostCredentials,
  validateBotTokenAndResolveChannel,
} from "../commands/comm-connect.js";
import { fetchBotTokenFromServer } from "./fetch-mattermost-bot-token-from-server.js";
import { getBotToken, saveBotToken } from "./mattermost-token-keychain-store.js";
import type { AmbiguousChannelCandidate } from "./resolve-mattermost-bot-channel-via-api-chain.js";
import { MattermostSetupError } from "./resolve-mattermost-bot-channel-via-api-chain.js";
import { log } from "./terminal-logger.js";

export interface CommSetupPhaseArgs {
  workspacePath: string;
}

export type CommSetupResult = { ok: true; channelName: string } | { ok: false; reason: string };

// Prompt gọn (Yes/No → chọn kênh → URL/bot/channel/token) → resolve + validate (lõi
// comm-connect) → persist. Ném lỗi để orchestrator catch fail-soft (trừ user skip).
export async function runCommSetupPhase(args: CommSetupPhaseArgs): Promise<CommSetupResult> {
  const wantsSetup = await confirm({
    message: "Bạn có muốn setup giao tiếp giữa DEV và PO không?",
    default: false,
  });
  if (!wantsSetup) {
    return { ok: false, reason: "skipped" };
  }

  const channel = await select({
    message: "Chọn kênh giao tiếp:",
    choices: [
      { name: "Mattermost (https://mm.nal.vn)", value: "mattermost" },
      { name: "Hủy", value: "cancel" },
    ],
  });
  if (channel === "cancel") {
    return { ok: false, reason: "skipped" };
  }

  log.info("Setup Mattermost cho workspace...");

  const mattermostUrl = (
    await input({ message: "Mattermost URL:", default: DEFAULT_MM_URL })
  ).trim();
  const botName = (
    await input({ message: "Tên bot Mattermost:", default: DEFAULT_BOT_NAME })
  ).trim();
  let channelName = (
    await input({
      message: "Tên channel chat (hoặc team/channel):",
      validate: (v) => (v.trim().length > 0 ? true : "Không được rỗng"),
    })
  ).trim();

  // Thứ tự ưu tiên cho bot mặc định "avatar": (1) Keychain — nhanh, offline; (2)
  // miss → server NAL get-bot-token — cần đã `avatar login`; (3) cả 2 fail → hỏi
  // tay. Bot khác "avatar" giữ nguyên: Keychain → hỏi tay (server chỉ có token avatar).
  const savedToken = botName === DEFAULT_BOT_NAME ? await getBotToken(botName) : null;
  const serverToken =
    !savedToken && botName === DEFAULT_BOT_NAME ? await fetchBotTokenFromServer(botName) : null;
  let token: string;
  if (savedToken) {
    log.info("Dùng bot token @avatar đã lưu trong keychain.");
    token = savedToken;
  } else if (serverToken) {
    log.info("Đã lấy bot token @avatar từ server NAL.");
    token = serverToken;
    await saveBotToken(botName, token);
  } else {
    if (botName === DEFAULT_BOT_NAME) {
      log.dim("Chưa login? Chạy avatar login, hoặc dán token thủ công:");
    }
    token = (await password({ message: "Dán bot token Mattermost:", mask: "*" })).trim();
    if (!token) {
      throw new Error("Token rỗng — hủy Setup Chatops.");
    }
  }

  // Retry-loop: validate fail → menu sửa (điền lại channel / nhập lại token) hoặc
  // defer setup sau (`avatar chatops-connect`). GIỮ url/bot đã nhập — chỉ re-prompt
  // field liên quan tới loại lỗi. Pass (break) → tiếp tục persist bên dưới.
  let resolved: Awaited<ReturnType<typeof validateBotTokenAndResolveChannel>>;
  for (;;) {
    try {
      resolved = await validateBotTokenAndResolveChannel(
        mattermostUrl,
        token,
        botName,
        channelName,
        async (candidates: AmbiguousChannelCandidate[]) =>
          select({
            message: `Channel có ở ${candidates.length} team — chọn:`,
            choices: candidates.map((c) => ({
              name: `${c.teamName}/${channelName} (${c.teamDisplayName})`,
              value: c,
            })),
          }),
      );
      break;
    } catch (err) {
      const kind = err instanceof MattermostSetupError ? err.kind : "other";
      const message = err instanceof Error ? err.message : String(err);
      log.warn(message);

      const isTokenError = kind === "token";
      const fixChoice = await select({
        message: "Xử lý thế nào?",
        choices: [
          {
            name: isTokenError ? "Nhập lại token" : "Điền lại tên channel",
            value: "retry",
          },
          { name: "Setup lại sau với lệnh avatar chatops-connect", value: "defer" },
        ],
      });

      if (fixChoice === "defer") {
        return { ok: false, reason: "deferred" };
      }

      // retry: re-prompt field tương ứng loại lỗi, giữ nguyên các field khác.
      if (isTokenError) {
        token = (await password({ message: "Dán bot token Mattermost:", mask: "*" })).trim();
        if (!token) {
          throw new Error("Token rỗng — hủy Setup Chatops.");
        }
      } else {
        channelName = (
          await input({
            message: "Tên channel chat (hoặc team/channel):",
            validate: (v) => (v.trim().length > 0 ? true : "Không được rỗng"),
          })
        ).trim();
      }
      // loop lại để validate với field đã re-prompt.
    }
  }

  // Validate thành công → lưu lại token cho lần sau (kể cả bot khác avatar).
  await saveBotToken(botName, token);

  const creds: MattermostCredentials = {
    mattermostUrl,
    botName,
    botToken: token,
    teamId: resolved.teamId,
    defaultChannelId: resolved.channelId,
    defaultChannelName: resolved.channelName,
  };
  await persistMattermostCredentials(args.workspacePath, creds);
  log.success(`Mattermost ready · channel: ${resolved.channelName}`);
  return { ok: true, channelName: resolved.channelName };
}
